import SEO from "@/components/SEO";
import HeroSection from "@/components/HeroSection";
import ServiceCard from "@/components/ServiceCard";
import ContactForm from "@/components/ContactForm";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { 
  Code, 
  Cpu, 
  Cloud,
  Globe, 
  Mail, 
  MapPin, 
  Phone, 
  Shield, 
  Smartphone, 
  Target, 
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  Calendar,
  Linkedin,
  Twitter,
  Instagram,
  ChevronRight,
  ArrowRight,
  PenTool,
  BarChart,
  Briefcase,
  HeartPulse,
  GraduationCap,
  Rocket,
  Star,
  ShoppingCart,
  BarChart2,
  Layout
} from "lucide-react";
import { useState, useEffect } from "react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function Home() {
  const technologies = [
    { name: "React", icon: Code, color: "text-blue-600" },
    { name: "Node.js", icon: Cpu, color: "text-green-600" },
    { name: "Python", icon: Code, color: "text-yellow-600" },
    { name: "TypeScript", icon: Code, color: "text-blue-700" },
    { name: "AWS", icon: Cloud, color: "text-orange-600" },
    { name: "Docker", icon: Cpu, color: "text-blue-700" },
  ];

  interface StatItem {
    value: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }

  const stats: StatItem[] = [
    { value: "50+", label: "Projects Completed", icon: Target, color: "text-blue-500" },
    { value: "30+", label: "Happy Clients", icon: Users, color: "text-green-500" },
    { value: "5+", label: "Years Experience", icon: TrendingUp, color: "text-purple-500" },
    { value: "24/7", label: "Support", icon: Shield, color: "text-orange-500" },
  ];

  const testimonials = [
    {
      quote: "First Soft UAE transformed our online presence completely. Their team delivered beyond our expectations.",
      author: "Ahmed Al Maktoum",
      role: "CEO, TechGulf Solutions"
    },
    {
      quote: "The mobile app they developed helped us increase our customer engagement by 150% in just 3 months.",
      author: "Sarah Johnson",
      role: "Product Manager, RetailPlus"
    },
    {
      quote: "Professional, reliable, and innovative. First Soft UAE is our go-to partner for all tech solutions.",
      author: "Mohammed Hassan",
      role: "CTO, FinTech Pro"
    }
  ];

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const services = [
    {
      title: "Web Development",
      description: "High-performance websites with modern frameworks (React, Next.js) optimized for UAE businesses",
      icon: <Code className="h-8 w-8 text-blue-600" />,
      badge: 'Most Popular'
    },
    {
      title: 'UI/UX Design',
      description: 'Award-winning interfaces that convert visitors into customers with Arabic/English support',
      icon: <Layout className="h-8 w-8 text-purple-600" />,
      badge: 'Trending'
    },
    {
      title: 'Mobile Apps',
      description: 'iOS & Android apps built with React Native for seamless cross-platform performance',
      icon: <Smartphone className="h-8 w-8 text-cyan-600" />,
      badge: 'Best Value'
    },
    {
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and deployment solutions',
      icon: <Cloud className="h-8 w-8 text-cyan-600" />,
      gradient: "from-blue-500 to-cyan-400",
      slug: "cloud-services"
    },
    {
      title: 'Cyber Security',
      description: 'Protect your digital assets with our security solutions',
      icon: <Shield className="h-8 w-8 text-cyan-600" />,
      gradient: "from-green-600 to-emerald-500",
      slug: "cyber-security"
    },
    {
      title: 'Data Analytics',
      description: 'Actionable insights and business intelligence from your data',
      icon: <BarChart2 className="h-8 w-8 text-cyan-600" />,
      gradient: "from-purple-500 to-pink-500",
      slug: "data-analytics"
    }
  ];

  const optimizedVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 100,
        mass: 0.5,
        delay: 0.1
      }
    }
  };

  return (
    <>
      <SEO
        title="First Soft UAE | Web Design & App Development in Dubai, UAE"
        description="First Soft UAE offers custom web design, mobile app, and software development services in UAE. Fast, modern, and built for business growth."
        keywords="software development company UAE, web design Dubai, mobile app development Sharjah, digital marketing UAE, First Soft UAE"
      />

      <HeroSection />
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="font-orbitron font-bold text-3xl md:text-4xl mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-purple-600 to-cyan-600 animate-gradient-x">
              Web Design UAE | Website, App & Software Company – First Soft UAE
              </span>
            </h1>
            <p className="text-xl text-gray-800 max-w-3xl mx-auto">
              We deliver enterprise-grade custom software development services designed specifically for the UAE market, helping businesses streamline operations and drive digital transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Web Development',
                description: 'High-performance websites with modern frameworks (React, Next.js) optimized for UAE businesses',
                icon: <Code className="h-8 w-8 text-blue-700" />,
                badge: 'Most Popular'
              },
              {
                title: 'UI/UX Design',
                description: 'Award-winning interfaces that convert visitors into customers with Arabic/English support',
                icon: <Layout className="h-8 w-8 text-purple-700" />,
                badge: 'Trending'
              },
              {
                title: 'Mobile Apps',
                description: 'iOS & Android apps built with React Native for seamless cross-platform performance',
                icon: <Smartphone className="h-8 w-8 text-cyan-700" />,
                badge: 'Best Value'
              }
            ].map((service, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 relative overflow-hidden border border-gray-100"
              >
                {service.badge && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-700 to-purple-700 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {service.badge}
                  </div>
                )}
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-blue-50 to-purple-50 mb-6">
                  {service.icon}
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h2>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link 
                  href={
                    service.title === 'Web Development' ? '/web-design-development' :
                    service.title === 'UI/UX Design' ? '/ui-ux' :
                    '/mobile-app-development'
                  }
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-sm"
                  aria-label={`Learn more about ${service.title}`}
                >
                  Learn more about {service.title.toLowerCase()} <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link 
              href="/services" 
              className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 transition-all duration-300 min-h-[56px]"
            >
              Explore All Services
              <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 relative overflow-hidden bg-white">
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={optimizedVariants}
              viewport={{ once: true, margin: "-100px" }}
              className="lg:col-span-7"
            >
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
                Pioneering Digital Excellence
              </div>
              <h2 className="font-orbitron font-bold text-3xl sm:text-4xl md:text-5xl mb-6 relative inline-block">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-500 to-cyan-500 animate-gradient-x bg-[length:300%_100%] tracking-wide">
                  We Architect Tomorrow's Tech
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-600/30 via-purple-500/30 to-cyan-500/30 blur-lg scale-110 animate-pulse-slow opacity-70"></span>
              </h2>
              
              <div className="space-y-6 mb-8">
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                  Founded in 2017 and based in Sharjah, First Soft UAE has emerged as a leader in crafting transformative digital solutions. Our journey began with a simple vision: to bridge the gap between innovative ideas and cutting-edge technology.
                </p>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                  Today, we're proud to be the driving force behind digital transformation for businesses across the UAE and beyond, delivering solutions that not only meet but exceed expectations.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { value: '50+', label: 'Projects Delivered', icon: CheckCircle },
                  { value: '30+', label: 'Happy Clients', icon: Users },
                  { value: '5+', label: 'Years Experience', icon: Calendar },
                  { value: '24/7', label: 'Support', icon: Clock }
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div 
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + (i * 0.1) }}
                      className="bg-card p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xl font-bold">{stat.value}</div>
                          <div className="text-xs text-muted-foreground">{stat.label}</div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-8 py-4">
                <Link href="/about" className="inline-flex min-h-[72px] items-center justify-center px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors border border-white/20 text-white font-medium text-lg" aria-label="Discover Our Story">
                  Discover Our Story
                  <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link href="/about" className="inline-flex min-h-[72px] items-center justify-center px-6 py-3 rounded-lg bg-white text-gray-900 hover:bg-gray-100 transition-colors font-medium text-lg" aria-label="Meet Our Team">
                  Meet Our Team
                </Link>
              </div>
            </motion.div>

            <motion.div className="lg:col-span-5 relative" style={{ willChange: 'transform, opacity' }}>
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-border/50 bg-white">
                <img
                  src="https://res.cloudinary.com/due4nhetn/image/upload/v1752432888/photo-1522071820081-009f0129c71c_1_1_fxkduh.webp"
                  alt="First Soft UAE team collaborating on a project"
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full border border-gray-300/80">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-white"></div>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-800">Our Dedicated Team</span>
                  </div>
                </div>
              </div>
              
              <motion.div 
                className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl -z-10"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              ></motion.div>
              
              <motion.div 
                className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-2xl -z-10"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              ></motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.05 }}
                  className="text-center p-6 bg-card rounded-xl border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg text-center"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${stat.color.replace('text-', 'bg-')}/10`}>
                    <Icon className={`w-8 h-8 ${stat.color}`} />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">{stat.value}</h3>
                  <p className="text-muted-foreground">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-orbitron font-bold text-3xl md:text-4xl mb-4 relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-500 to-cyan-500 animate-gradient-x bg-[length:300%_100%] tracking-wide">
                Trusted by <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-500 to-cyan-500 animate-gradient-x">Industry Leaders</span>
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-blue-600/30 via-purple-500/30 to-cyan-500/30 blur-lg scale-110 animate-pulse-slow opacity-70"></span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We've helped businesses across industries achieve their digital transformation goals
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
            {[
              { icon: <Code className="w-8 h-8 mx-auto text-white" />, industry: "Tech", clients: "50+", gradient: "from-blue-600 to-cyan-500" },
              { icon: <Briefcase className="w-8 h-8 mx-auto text-white" />, industry: "Finance", clients: "30+", gradient: "from-purple-600 to-indigo-500" },
              { icon: <HeartPulse className="w-8 h-8 mx-auto text-white" />, industry: "Healthcare", clients: "15+", gradient: "from-rose-600 to-pink-500" },
              { icon: <ShoppingCart className="w-8 h-8 mx-auto text-white" />, industry: "E-commerce", clients: "25+", gradient: "from-amber-600 to-orange-500" },
              { icon: <GraduationCap className="w-8 h-8 mx-auto text-white" />, industry: "Education", clients: "20+", gradient: "from-emerald-600 to-teal-500" },
            ].map((item, index) => (
              <motion.div 
                key={index}
                className={`bg-gradient-to-r ${item.gradient} p-6 rounded-xl text-white text-center`}
                whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg mb-1 text-white">{item.industry}</h3>
                <p className="text-white/90">{item.clients} Clients</p>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Rocket className="w-8 h-8" />, metric: "100+", label: "Projects" },
              { icon: <Users className="w-8 h-8" />, metric: "50+", label: "Clients" },
              { icon: <Star className="w-8 h-8" />, metric: "98%", label: "Satisfaction" },
              { icon: <Shield className="w-8 h-8" />, metric: "24/7", label: "Support" },
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 p-6 rounded-xl text-white text-center"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-bold text-white text-3xl mb-1">{item.metric}</h3>
                <p className="text-white/90" >{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={optimizedVariants}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Our Stack
            </span>
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-6">
              Technologies We Master
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We leverage cutting-edge technologies to build scalable, secure, and innovative solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.15, delay: index * 0.05 }}
                  className="group p-6 bg-card rounded-xl border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg text-center"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${tech.color.replace('text-', 'bg-')}/10 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-8 h-8 ${tech.color}`} />
                  </div>
                  <p className="font-medium">{tech.name}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={optimizedVariants}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Client Testimonials
            </span>
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-6">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.25 }}
                className="bg-white p-8 md:p-10 rounded-2xl shadow-md border border-gray-200"
              >
                <div className="text-2xl md:text-3xl text-gray-700 leading-relaxed mb-8">
                  "{testimonials[activeTestimonial].quote}"
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonials[activeTestimonial].author.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{testimonials[activeTestimonial].author}</h3>
                    <p className="text-gray-600">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center mt-8 space-x-4">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${activeTestimonial === index ? 'bg-blue-600' : 'bg-gray-300'}`}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-xl" style={{ willChange: 'transform' }}>
            <div className="relative z-10 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Ready to Start Your Project?</h2>
              <p className="text-gray-700 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
                Let's discuss how we can help you achieve your business goals with our expert solutions.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <Link href="/contact" className="inline-flex min-h-[60px] items-center justify-center">
                  <Button size="lg" className="min-h-[60px] bg-blue-800 text-white hover:bg-blue-900 hover:scale-[1.03] transform transition-all duration-200 px-10 py-7 text-lg font-semibold rounded-xl shadow-md" aria-label="Get Started">
                    Get Started <ArrowRight className="ml-3 h-5 w-5" />
                  </Button>
                </Link>
                <Link href="/services" className="inline-flex min-h-[60px] items-center justify-center">
                  <Button size="lg" variant="outline" className="min-h-[60px] bg-transparent border-2 border-gray-700 text-gray-800 hover:bg-gray-50 hover:border-gray-800 hover:text-black hover:scale-[1.03] transform transition-all duration-200 px-10 py-7 text-lg font-semibold rounded-xl" aria-label="Our Services">
                    Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pt-16 pb-8 md:pt-20 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={optimizedVariants}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-6">Get In Touch</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ready to start your next project? Let's discuss how we can help architect your digital future.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="glass-effect p-6 md:p-8 rounded-2xl border border-gray-200 shadow-xl">
                <h3 className="font-orbitron font-semibold text-2xl mb-8 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  <a 
                    href="https://maps.app.goo.gl/DgTfJ51jwL9Ma5wV7" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group flex items-start space-x-4 hover:translate-x-1 transition-transform duration-200"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">Our Location</h4>
                      <p className="text-muted-foreground group-hover:text-foreground transition-colors">
                        Almawarid Building, Industrial Area 3<br />
                        Sharjah, United Arab Emirates
                      </p>
                    </div>
                  </a>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1">Phone Number</h4>
                      <a href="tel:+971582481443" className="text-purple-500 hover:underline whitespace-nowrap font-medium">
                        +971-58-248-1443
                      </a>
                    </div>
                  </div>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                  
                  <a 
                    href="mailto:info@firstsoft.ae" 
                    className="group flex items-start space-x-4 hover:translate-x-1 transition-transform duration-200"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1 group-hover:text-accent transition-colors">Email Address</h4>
                      <p className="text-muted-foreground group-hover:text-foreground transition-colors">
                        info@firstsoft.ae
                      </p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://firstsoft.ae" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group flex items-start space-x-4 hover:translate-x-1 transition-transform duration-200"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                      <Globe className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1 group-hover:text-accent transition-colors">Website</h4>
                      <p className="text-muted-foreground group-hover:text-foreground transition-colors">
                        www.firstsoft.ae
                      </p>
                    </div>
                  </a>
                </div>
                
                <div className="mt-10">
                  <h4 className="font-semibold text-lg mb-4">Business Hours</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monday - Friday</span>
                      <span className="font-medium">9:00 AM - 10:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Saturday</span>
                      <span className="font-medium">Closed</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sunday</span>
                      <span className="font-medium">Closed</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Static Map with Link */}
              <div className="mt-4 rounded-2xl overflow-hidden border border-gray-200 shadow-xl">
                <a 
                  href="https://maps.app.goo.gl/DgTfJ51jwL9Ma5wV7" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block relative group"
                >
                  <div className="relative w-full h-64 bg-white flex items-center justify-center">
                    <div className="text-center p-6">
                      <svg className="w-12 h-12 mx-auto text-primary mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h3 className="text-lg font-semibold mb-2 text-gray-900">First Soft UAE Location</h3>
                      <p className="text-sm text-muted-foreground">Click to view on Google Maps</p>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-white/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="text-gray-900 text-sm font-medium">View on Google Maps →</span>
                    </div>
                  </div>
                </a>
                <div className="bg-white p-4 text-center border-t border-gray-200">
                  <p className="text-sm text-gray-700">Almawarid Building, Industrial Area 3, Sharjah, UAE</p>
                </div>
              </div>

              <div className="flex space-x-4 mt-8">
                <a 
                  href="https://linkedin.com/company/firstsoftuae" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-700 hover:text-white transition-colors text-white/90 hover:scale-105 transform transition-transform"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-6 h-6" />
                </a>
                <a 
                  href="https://twitter.com/firstsoftuae" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors text-white/90 hover:scale-105 transform transition-transform"
                  aria-label="Twitter"
                >
                  <Twitter className="w-6 h-6" />
                </a>
                <a 
                  href="https://instagram.com/firstsoftuae" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center hover:bg-pink-700 hover:text-white transition-colors text-white/90 hover:scale-105 transform transition-transform"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </motion.div>

            <div className="relative">
              <ContactForm />
              {/* Decorative tech pattern */}
              <div className="absolute -bottom-8 -right-8 w-40 h-40 opacity-10">
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <path fill="currentColor" d="M100,20 L120,60 L180,60 L130,90 L150,130 L100,100 L50,130 L70,90 L20,60 L80,60 Z" />
                  <circle cx="100" cy="100" r="10" fill="currentColor" />
                  <circle cx="50" cy="50" r="5" fill="currentColor" />
                  <circle cx="150" cy="50" r="5" fill="currentColor" />
                  <circle cx="50" cy="150" r="5" fill="currentColor" />
                  <circle cx="150" cy="150" r="5" fill="currentColor" />
                </svg>
              </div>
            </div>
          </div>
          {/* Decorative gradient blur */}
          <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full filter blur-3xl -z-10"></div>
        </div>
      </section>

      <WebDesignLocations />
    </>
  );
}
